import React, { useEffect, useMemo, useRef, useState } from 'react';
import api, { extractUploadedPlatformAssetPath, getPlatformAssetUrl } from '../../utils/api';
import { prepareFileForUpload } from '../../utils/imageUploadCompress';
import { usePlatformBranding } from '../../context/PlatformBrandingContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Settings as SettingsIcon, Save, Image, Menu, Loader2, Trash2 } from 'lucide-react';
import SettingsSidebar from './SettingsSidebar';
import SettingsSectionHeader from './SettingsSectionHeader';
import { resolveSettingsNav, firstAccessibleSettingsSection } from '../../utils/settingsPermissions';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { DEFAULT_FOOTER_TAGLINE } from '../../context/PlatformBrandingContext';
import PlatformPaymentSettings from './PlatformPaymentSettings';
import PlatformSmsSettings from './PlatformSmsSettings';
import PlatformFinancialYearSettings from './PlatformFinancialYearSettings';
import PlatformSecuritySettings from './PlatformSecuritySettings';

const PRIMARY_BTN = 'bg-blue-600 hover:bg-blue-700 text-white';

function SettingsCard({ children, className = '' }) {
  return (
    <Card className={`rounded-2xl border-border shadow-sm ${className}`}>
      {children}
    </Card>
  );
}

export default function PlatformSettings() {
  const { user } = useAuth();
  const { branding, refreshPlatformBranding, patchPlatformBranding } = usePlatformBranding();
  const { toast } = useToast();
  const settingsNav = useMemo(() => resolveSettingsNav(user), [user]);
  const [activeSection, setActiveSection] = useState(() => firstAccessibleSettingsSection(user) || 'platform.branding');
  const [expandedGroup, setExpandedGroup] = useState('platform');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDeleting, setLogoDeleting] = useState(false);
  const [form, setForm] = useState({ name: '', footer_tagline: DEFAULT_FOOTER_TAGLINE });
  const [logoPath, setLogoPath] = useState(null);
  const [logoVersion, setLogoVersion] = useState(0);
  const logoInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('platform/settings')
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setForm({
          name: d.name || '',
          footer_tagline: d.footer_tagline || DEFAULT_FOOTER_TAGLINE,
        });
        setLogoPath(d.logo || null);
      })
      .catch(() => {
        if (!cancelled) {
          toast({
            title: 'Error',
            description: 'Failed to load platform settings',
            variant: 'destructive',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  useEffect(() => {
    if (!branding) return;
    setForm({
      name: branding.name || '',
      footer_tagline: branding.footer_tagline || DEFAULT_FOOTER_TAGLINE,
    });
    setLogoPath(branding.logo || null);
  }, [branding]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('platform/settings', {
        name: form.name,
        footer_tagline: form.footer_tagline,
      });
      await refreshPlatformBranding();
      toast({ title: 'Saved', description: 'Platform settings updated.' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to save platform settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Choose an image (JPG, PNG, GIF, WebP)',
        variant: 'destructive',
      });
      return;
    }
    setLogoUploading(true);
    try {
      const prepared = await prepareFileForUpload(file);
      const formData = new FormData();
      formData.append('logo', prepared);
      const res = await api.post('platform/logo', formData);
      const uploadData = res.data?.data ?? {};
      const path = extractUploadedPlatformAssetPath(uploadData, 'logo');
      if (path) {
        setLogoPath(path);
        setLogoVersion((v) => v + 1);
        patchPlatformBranding({ logo: path });
        await refreshPlatformBranding();
        toast({ title: 'Success', description: 'Platform logo uploaded' });
      } else {
        toast({
          title: 'Upload incomplete',
          description: 'File may be saved but settings were not updated. Retry.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || err.message || 'Upload failed',
        variant: 'destructive',
      });
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleLogoDelete = async () => {
    setLogoDeleting(true);
    try {
      await api.delete('platform/logo');
      setLogoPath(null);
      setLogoVersion((v) => v + 1);
      patchPlatformBranding({ logo: null });
      await refreshPlatformBranding();
      toast({ title: 'Success', description: 'Platform logo removed' });
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to remove platform logo',
        variant: 'destructive',
      });
    } finally {
      setLogoDeleting(false);
    }
  };

  const logoPreviewUrl = getPlatformAssetUrl(logoPath, logoVersion);
  const logoBusy = logoUploading || logoDeleting;

  const sidebarProps = {
    navGroups: settingsNav,
    activeSection,
    expandedGroup,
    onSelectSection: (id) => {
      setActiveSection(id);
      setMobileNavOpen(false);
    },
    onToggleGroup: (key) => setExpandedGroup((prev) => (prev === key ? '' : key)),
  };

  return (
    <div className="space-y-6" data-testid="platform-settings-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <SettingsIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" /> Settings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Configure platform-wide settings for the admin console
          </p>
        </div>
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" className="lg:hidden rounded-lg">
              <Menu className="h-4 w-4 mr-2" /> Settings menu
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[min(100vw,20rem)] p-0">
            <SheetHeader className="p-4 border-b"><SheetTitle>Settings</SheetTitle></SheetHeader>
            <SettingsSidebar {...sidebarProps} className="border-0 rounded-none shadow-none" />
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <SettingsSidebar {...sidebarProps} className="hidden lg:block" />
        <main className="flex-1 min-w-0 w-full">
          <SettingsSectionHeader
            sectionId={activeSection}
            description={
              activeSection === 'platform.payment'
                ? 'Connect Razorpay to accept subscription payments from Indian schools.'
                : activeSection === 'platform.sms'
                  ? 'Configure Twilio for mobile OTP verification on web and mobile apps.'
                  : activeSection === 'platform.financialYear'
                    ? 'Create financial years for CRM reports and salaries. Only active years appear in the top navbar picker.'
                    : activeSection === 'platform.security'
                      ? 'Extra safety controls for destructive Super Admin actions.'
                      : 'Logo and labels shown in the sidebar and footer when no school is selected.'
            }
          />
          {activeSection === 'platform.payment' ? (
            <SettingsCard>
              <PlatformPaymentSettings />
            </SettingsCard>
          ) : activeSection === 'platform.sms' ? (
            <SettingsCard>
              <PlatformSmsSettings />
            </SettingsCard>
          ) : activeSection === 'platform.financialYear' ? (
            <PlatformFinancialYearSettings />
          ) : activeSection === 'platform.security' ? (
            <SettingsCard>
              <PlatformSecuritySettings />
            </SettingsCard>
          ) : loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
          ) : (
            <SettingsCard>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4" /> Platform Logo
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Replaces the default sidebar logo for super admin sessions.
                  </p>
                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleLogoPick}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoBusy}
                      className="rounded-lg"
                    >
                      {logoUploading ? 'Uploading…' : 'Choose image'}
                    </Button>
                    {logoPreviewUrl ? (
                      <>
                        <img
                          src={logoPreviewUrl}
                          alt="Platform logo preview"
                          className="h-14 max-w-[200px] object-contain rounded-lg border border-border bg-muted/30 p-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleLogoDelete}
                          disabled={logoBusy}
                          className="rounded-lg text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {logoDeleting ? 'Removing…' : 'Remove logo'}
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Label>Display name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-1.5 rounded-lg"
                    placeholder="School Analytics CPRawat"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown beside the logo in the sidebar.
                  </p>
                </div>
                <div>
                  <Label>Footer tagline</Label>
                  <Input
                    value={form.footer_tagline}
                    onChange={(e) => setForm({ ...form, footer_tagline: e.target.value })}
                    className="mt-1.5 rounded-lg"
                    placeholder={DEFAULT_FOOTER_TAGLINE}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown below the footer logo on every page.
                  </p>
                </div>
                <Button onClick={handleSave} className={PRIMARY_BTN} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save settings'}
                </Button>
              </CardContent>
            </SettingsCard>
          )}
        </main>
      </div>
    </div>
  );
}
